const anywellRealVideoScriptUrl = document.currentScript?.src || "";

class AnywellRealVideo extends HTMLElement {
  connectedCallback() {
    if (this.dataset.awReady === "true") return;
    this.dataset.awReady = "true";
    this.querySelector("button")?.addEventListener("click", () => this.loadPlayer());
  }

  loadPlayer() {
    if (this.querySelector("video")) return;
    const template = this.querySelector("template");
    const media = this.querySelector(".aw-real-video__media");
    if (!template || !media) return;

    const fragment = template.content.cloneNode(true);
    const video = fragment.querySelector("video");
    if (!video) return;

    this.bindTracking(video);
    media.replaceChildren(video);
    video.play().catch(() => {
      video.controls = true;
    });
  }

  bindTracking(video) {
    const reached = new Set();
    video.addEventListener("play", () => {
      document.querySelectorAll("aw-real-video video").forEach((otherVideo) => {
        if (otherVideo !== video && !otherVideo.paused) otherVideo.pause();
      });
      if (!reached.has("play")) {
        reached.add("play");
        this.track("anywell_video_play");
      }
    });

    video.addEventListener("timeupdate", () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      const progress = video.currentTime / video.duration;
      if (progress >= 0.25 && !reached.has("25")) {
        reached.add("25");
        this.track("anywell_video_25");
      }
      if (progress >= 0.75 && !reached.has("75")) {
        reached.add("75");
        this.track("anywell_video_75");
      }
    });

    video.addEventListener("ended", () => {
      if (reached.has("complete")) return;
      reached.add("complete");
      this.track("anywell_video_complete");
    });
  }

  track(eventName) {
    const detail = {
      event: eventName,
      video_key: this.dataset.videoKey || "",
      product_handle: this.dataset.productHandle || ""
    };
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
    this.dispatchEvent(new CustomEvent("anywell:product-video", {
      bubbles: true,
      detail
    }));
  }
}

if (!customElements.get("aw-real-video")) {
  customElements.define("aw-real-video", AnywellRealVideo);
}

const ys16SupplementVideos = [
  {
    key: "quick_setup_and_ride",
    title: "Quick setup and ride",
    description: "Watch the chair being prepared, seated, and driven outdoors.",
    ariaLabel: "Play quick setup and ride video",
    alt: "YS16-1 MAX being prepared for an outdoor ride",
    poster: "anywell-ys16-real-quick-setup-poster-v1.webp",
    video: "anywell-ys16-real-quick-setup-v1.mp4"
  },
  {
    key: "frame_and_battery_closeup",
    title: "Frame and battery close-up",
    description: "See the frame joints, suspension points, battery, and power connector.",
    ariaLabel: "Play frame and battery close-up video",
    alt: "Close-up of the YS16-1 MAX folding frame and suspension",
    poster: "anywell-ys16-real-frame-closeup-poster-v1.webp",
    video: "anywell-ys16-real-frame-closeup-v1.mp4"
  },
  {
    key: "battery_connection_details",
    title: "Battery connection details",
    description: "See how the battery is positioned and connected behind the seat.",
    ariaLabel: "Play battery connection details video",
    alt: "YS16-1 MAX battery connector behind the seat",
    poster: "anywell-ys16-real-battery-access-poster-v1.webp",
    video: "anywell-ys16-real-battery-access-v1.mp4"
  }
];

function realVideoAssetUrl(fileName) {
  const scriptUrl = anywellRealVideoScriptUrl
    || document.querySelector('script[src*="anywell-real-life-video.js"]')?.src;
  return scriptUrl ? new URL(fileName, scriptUrl).href : fileName;
}

function createSupplementVideoCard(video, productHandle) {
  const card = document.createElement("aw-real-video");
  card.dataset.videoKey = video.key;
  card.dataset.productHandle = productHandle;

  const media = document.createElement("div");
  media.className = "aw-real-video__media";
  const button = document.createElement("button");
  button.className = "aw-real-video__poster";
  button.type = "button";
  button.setAttribute("aria-label", video.ariaLabel);
  const image = document.createElement("img");
  image.src = realVideoAssetUrl(video.poster);
  image.alt = video.alt;
  image.width = 720;
  image.height = 900;
  image.loading = "lazy";
  image.decoding = "async";
  const play = document.createElement("span");
  play.className = "aw-real-video__play";
  play.setAttribute("aria-hidden", "true");
  play.innerHTML = '<svg aria-hidden="true" width="64" height="64" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="11"></circle><path d="M10 8.5v7l5-3.5-5-3.5Z" fill="white"></path></svg>';
  button.append(image, play);

  const template = document.createElement("template");
  const player = document.createElement("video");
  player.controls = true;
  player.playsInline = true;
  player.preload = "metadata";
  player.poster = image.src;
  const source = document.createElement("source");
  source.src = realVideoAssetUrl(video.video);
  source.type = "video/mp4";
  player.append(source);
  template.content.append(player);
  media.append(button, template);

  const copy = document.createElement("div");
  copy.className = "aw-real-video__copy";
  const title = document.createElement("h3");
  title.className = "aw-real-video__title";
  title.textContent = video.title;
  const description = document.createElement("p");
  description.className = "aw-real-video__text";
  description.textContent = video.description;
  copy.append(title, description);
  card.append(media, copy);
  return card;
}

function supplementYs16RealVideos() {
  const section = document.querySelector("[data-aw-real-video-section]");
  const grid = section?.querySelector(".aw-real-video-section__grid");
  if (!section || !grid) return;

  const firstCard = grid.querySelector("aw-real-video");
  const productHandle = firstCard?.dataset.productHandle || "";
  const supportedHandles = new Set([
    "ys16-1-max-heavy-duty-wide-seat-power-wheelchair-12ah",
    "anywell-ys16-max-wide-seat-electric-wheelchair-20ah"
  ]);
  if (!supportedHandles.has(productHandle)) return;

  const existingKeys = new Set(
    [...grid.querySelectorAll("aw-real-video")].map((card) => card.dataset.videoKey)
  );
  for (const video of ys16SupplementVideos) {
    if (!existingKeys.has(video.key)) grid.append(createSupplementVideoCard(video, productHandle));
  }

  const realWorldDrive = grid.querySelector('[data-video-key="real_world_drive"]');
  if (realWorldDrive) grid.append(realWorldDrive);
  section.dataset.awRealVideoSupplemented = "true";

  if (!document.getElementById("aw-ys16-real-video-supplement-style")) {
    const style = document.createElement("style");
    style.id = "aw-ys16-real-video-supplement-style";
    style.textContent = "@media screen and (min-width:700px){[data-aw-real-video-supplemented] .aw-real-video-section__grid{grid-template-columns:repeat(3,minmax(0,320px));}}";
    document.head.append(style);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", supplementYs16RealVideos, { once: true });
} else {
  supplementYs16RealVideos();
}
